import { useState } from 'react';
import Card from '../ui/Card';
import Button from '../ui/Button';
import Input from '../ui/Input';
import { Sparkles, Loader2, Copy, Download, FileText, Globe } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import HistorySection from '../history/HistorySection';

const LANGUAGES = [
  { value: 'English', label: 'English' },
  { value: 'Hindi', label: 'Hindi (हिन्दी)' },
  { value: 'Kannada', label: 'Kannada (ಕನ್ನಡ)' },
  { value: 'Tamil', label: 'Tamil (தமிழ்)' },
  { value: 'Telugu', label: 'Telugu (తెలుగు)' },
  { value: 'Malayalam', label: 'Malayalam (മലയാളം)' },
  { value: 'Marathi', label: 'Marathi (मराठी)' },
  { value: 'Bengali', label: 'Bengali (বাংলা)' },
  { value: 'Gujarati', label: 'Gujarati (ગુજરાતી)' },
  { value: 'Punjabi', label: 'Punjabi (ਪੰਜਾਬੀ)' },
  { value: 'Spanish', label: 'Spanish' },
  { value: 'French', label: 'French' },
  { value: 'German', label: 'German' },
  { value: 'Arabic', label: 'Arabic' },
];

const AIBlogWriter = () => {
  const [topic, setTopic] = useState('');
  const [description, setDescription] = useState('');
  const [keywords, setKeywords] = useState('');
  const [tone, setTone] = useState('professional');
  const [language, setLanguage] = useState('English');
  const [wordCount, setWordCount] = useState(1500);
  const [useWebSearch, setUseWebSearch] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [content, setContent] = useState('');
  const [streamingContent, setStreamingContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [webSearchUsed, setWebSearchUsed] = useState(false);

  const handleGenerate = async () => {
    if (!topic.trim()) {
      setError('Please enter a blog topic');
      return;
    }

    setGenerating(true);
    setError(null);
    setContent('');
    setStreamingContent('');
    setWebSearchUsed(false);

    try {
      const keywordArray = keywords
        .split(',')
        .map((k) => k.trim())
        .filter((k) => k);

      const response = await fetch('/api/writing/blog-post', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          topic,
          description: description.trim() || undefined,
          keywords: keywordArray,
          tone,
          language,
          wordCount,
          useWebSearch,
        }),
      });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.error || 'Failed to generate blog post');
      }

      const reader = response.body?.getReader();
      const decoder = new TextDecoder();

      while (true) {
        const { done, value } = await reader!.read();
        if (done) break;

        for (const line of decoder.decode(value).split('\n')) {
          if (!line.startsWith('data: ')) continue;
          try {
            const data = JSON.parse(line.slice(6));
            if (data.type === 'chunk') {
              setStreamingContent((prev) => prev + (data.content || ''));
            } else if (data.type === 'complete') {
              setContent(data.content || '');
              setStreamingContent('');
              if (data.webSearchUsed) setWebSearchUsed(true);
            } else if (data.type === 'error') {
              setError(data.error || 'Generation failed');
            }
          } catch {
            // skip malformed lines
          }
        }
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setGenerating(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(content || streamingContent);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownload = () => {
    const blob = new Blob([content || streamingContent], { type: 'text/plain' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `blog_post_${Date.now()}.md`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const displayContent = content || streamingContent;

  return (
    <div className="max-w-5xl mx-auto space-y-8 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <div className="text-center space-y-4">
        <div className="inline-flex p-3 bg-gradient-to-br from-[#216974] to-[#216974] rounded-2xl shadow-lg shadow-[#216974]">
          <FileText size={32} className="text-white" />
        </div>
        <h1 className="text-4xl font-black text-gray-900 tracking-tight">AI Blog Writer</h1>
        <p className="text-gray-500 text-lg max-w-lg mx-auto font-medium">
          Generate SEO-optimized blog posts in any language, powered by real-time web data.
        </p>
      </div>

      <Card className="overflow-hidden border-0 shadow-2xl shadow-[#216974]">
        <div className="p-8 space-y-6">
          {/* Topic */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Blog Topic *</label>
            <Input
              placeholder="e.g., 10 Ways to Improve Your Productivity in 2025"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
              className="text-lg"
            />
          </div>

          {/* Description / Brief */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">
              Description / Brief <span className="text-gray-400 font-normal">(optional — tell AI what angle or key points to cover)</span>
            </label>
            <textarea
              placeholder="e.g., Focus on remote workers, cover tools like Notion and Todoist, include stats from recent studies, end with a motivational CTA"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent resize-none text-sm"
            />
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-bold text-gray-700 mb-2">Target Keywords <span className="text-gray-400 font-normal">(comma-separated)</span></label>
            <Input
              placeholder="e.g., productivity, time management, remote work"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              className="text-sm"
            />
          </div>

          {/* Language + Tone */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Language</label>
              <select
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent"
              >
                {LANGUAGES.map((l) => (
                  <option key={l.value} value={l.value}>{l.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">Tone</label>
              <select
                value={tone}
                onChange={(e) => setTone(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#54c4b6] focus:border-transparent"
              >
                <option value="professional">Professional</option>
                <option value="casual">Casual</option>
                <option value="trendy">Trendy</option>
                <option value="funny">Funny</option>
                <option value="inspiring">Inspiring</option>
                <option value="authoritative">Authoritative</option>
                <option value="empathetic">Empathetic</option>
                <option value="enthusiastic">Enthusiastic</option>
                <option value="informative">Informative</option>
                <option value="sarcastic">Sarcastic</option>
                <option value="serious">Serious</option>
                <option value="friendly">Friendly</option>
                <option value="formal">Formal</option>
                <option value="informal">Informal</option>
                <option value="persuasive">Persuasive</option>
                <option value="educational">Educational</option>
                <option value="entertaining">Entertaining</option>
                <option value="nostalgic">Nostalgic</option>
                <option value="urgent">Urgent</option>
                <option value="mysterious">Mysterious</option>
                <option value="bold">Bold</option>
                <option value="minimalist">Minimalist</option>
                <option value="luxury">Luxury</option>
                <option value="playful">Playful</option>
                <option value="sincere">Sincere</option>
                <option value="witty">Witty</option>
                <option value="curious">Curious</option>
              </select>
            </div>
          </div>

          {/* Word Count + Web Search */}
          <div className="grid grid-cols-2 gap-4 items-end">
            <div>
              <label className="block text-sm font-bold text-gray-700 mb-2">
                Word Count: {wordCount}
              </label>
              <input
                type="range"
                min="500"
                max="3000"
                step="100"
                value={wordCount}
                onChange={(e) => setWordCount(parseInt(e.target.value))}
                className="w-full mt-3"
              />
              <div className="flex justify-between text-xs text-gray-400 mt-1">
                <span>500</span><span>3000</span>
              </div>
            </div>
            <div>
              <label
                className={`flex items-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${
                  useWebSearch
                    ? 'border-[#54c4b6] bg-[#216974]'
                    : 'border-gray-200 bg-gray-50 hover:border-[#54c4b6]'
                }`}
              >
                <input
                  type="checkbox"
                  checked={useWebSearch}
                  onChange={(e) => setUseWebSearch(e.target.checked)}
                  className="w-4 h-4 accent-[#216974]"
                />
                <div className="flex items-center gap-2">
                  <Globe size={16} className={useWebSearch ? 'text-[#216974]' : 'text-gray-400'} />
                  <div>
                    <p className="text-sm font-bold text-gray-700">Web Search</p>
                    <p className="text-xs text-gray-400">Fetch real-time data on topic</p>
                  </div>
                </div>
              </label>
            </div>
          </div>

          <AnimatePresence>
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 font-semibold flex items-center gap-2"
              >
                <span>⚠️</span>
                {error}
              </motion.div>
            )}
          </AnimatePresence>

          <Button
            onClick={handleGenerate}
            isLoading={generating}
            className="w-full h-12 bg-gradient-to-r from-[#216974] to-[#216974] hover:scale-105 transition-transform text-white font-bold text-lg rounded-xl"
            leftIcon={generating ? <Loader2 className="animate-spin" size={20} /> : <Sparkles size={20} />}
          >
            {generating
              ? useWebSearch
                ? 'Searching Web & Generating...'
                : 'Generating Blog Post...'
              : 'Generate Blog Post'}
          </Button>
        </div>
      </Card>

      <AnimatePresence>
        {displayContent && (
          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 10 }}>
            <Card className="border-0 shadow-xl p-8 space-y-4">
              <div className="flex items-center justify-between mb-4">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Generated Blog Post</h2>
                  {webSearchUsed && (
                    <span className="inline-flex items-center gap-1 mt-1 text-xs font-semibold text-[#216974] bg-[#54c4b6]/15 px-2 py-0.5 rounded-full">
                      <Globe size={10} /> Written with real-time web data
                    </span>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={handleCopy}
                    className="flex items-center gap-2 px-4 py-2 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg font-semibold"
                    leftIcon={<Copy size={16} />}
                  >
                    {copied ? 'Copied!' : 'Copy'}
                  </Button>
                  {content && (
                    <Button
                      onClick={handleDownload}
                      className="flex items-center gap-2 px-4 py-2 bg-[#216974] hover:bg-[#216974]/10 text-[#216974] rounded-lg font-semibold"
                      leftIcon={<Download size={16} />}
                    >
                      Download
                    </Button>
                  )}
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-lg max-h-[500px] overflow-y-auto">
                {displayContent.split('\n').map((line, i) => {
                  if (line.startsWith('# '))
                    return <h1 key={i} className="text-2xl font-bold mt-4 mb-2 text-gray-900">{line.slice(2)}</h1>;
                  if (line.startsWith('## '))
                    return <h2 key={i} className="text-xl font-bold mt-3 mb-2 text-gray-800">{line.slice(3)}</h2>;
                  if (line.startsWith('### '))
                    return <h3 key={i} className="text-lg font-bold mt-2 mb-1 text-gray-800">{line.slice(4)}</h3>;
                  if (line.trim())
                    return <p key={i} className="text-gray-700 mb-2 leading-relaxed">{line}</p>;
                  return <br key={i} />;
                })}
                {streamingContent && !content && (
                  <span className="inline-block w-2 h-4 bg-[#216974] animate-pulse ml-0.5 align-middle" />
                )}
              </div>

              <p className="text-sm text-gray-500 text-center">
                ~{displayContent.split(/\s+/).filter(Boolean).length} words · Language: {language}
              </p>
            </Card>
          </motion.div>
        )}
      </AnimatePresence>

      <HistorySection toolId="ai-blog" />
    </div>
  );
};

export default AIBlogWriter;
